<template>
  <div id="group-editor-modal">
    
    <memlist-modal ref="edit-group" :title="group_name" size="xl">
      <div class="ml-tabs">
        <div class="ml-tabs-header">
          <button
            type="button"
            v-for="(tab, i) in tabs"
            :key="i"
            :class="['ml-tab-button', active_tab === i ? 'active' : '']"
            @click="active_tab = i"
            :disabled="tab.disabled"
          >
            {{ $t(tab.label) }}
          </button>
        </div>

        <div class="ml-tabs-body mb-4">
          <div v-show="active_tab === 0">
            <GroupEditorDetails
              v-if="local_group"
              class="mt-8"
              :group="local_group"
              @created="group_created"
              @change="group_changed"
            />
          </div>
          <div v-show="active_tab === 1">
            <GroupEditorCompanies
              v-if="local_group && local_group.id"
              class="mt-8"
              :group_id="local_group.id"
            />
          </div>
          <div v-show="active_tab === 2">
            <GroupEditorPages
              v-if="local_group && local_group.id"
              class="mt-8"
              :group="local_group"
              @created="created_group_page"
              @deleted="deleted_group_page"
            />
          </div>
          <div v-show="active_tab === 3">
            <GroupEditorPermissions
              v-if="local_group && local_group.id"
              class="mt-8"
              :group="local_group"
              :group_id="local_group.id"
              @permissions_changed="permissions_changed"
            />
          </div>
        
        </div>
      </div>

      <RightModalSaveAndCloseButtons
        class="mt-4"
        :text="$t('COMMON.SAVE')"
        :spin="true"
        @clicked="save_group_clicked"
        @close="$refs['edit-group'].hide()"
        ref="saveButton"
      />

    </memlist-modal>

  </div>
</template>

<script>

import axios from 'axios';
import { mapGetters } from 'vuex';
import { toasts } from '@/core/mixins/toastr-helper.mixin.js';
import { getPageNames } from '@/core/services/pagenames';
import GroupEditorDetails from '@/view/pages/ml/users/components/GroupEditorDetails.vue';
import GroupEditorCompanies from '@/view/pages/ml/users/components/GroupEditorCompanies.vue';
import GroupEditorPages from '@/view/pages/ml/users/components/GroupEditorPages.vue';
import GroupEditorPermissions from '@/view/pages/ml/users/components/GroupEditorPermissions.vue';
import RightModalSaveAndCloseButtons from '@/view/components/buttons/RightModalSaveAndCloseButtons.vue';

export default {
  name: 'group-editor-modal',
  components: {
    GroupEditorDetails,
    GroupEditorCompanies,
    GroupEditorPages,
    GroupEditorPermissions,
    RightModalSaveAndCloseButtons
  },
  props: ['group_id'],
  mixins: [ toasts ],
  computed: {

    tabs() {
      let base = [
        { label: 'ACCESS.GROUP.DETAILS', disabled: false },
        { label: 'ACCESS.GROUP.COMPANIES', disabled: this.group_id === null },
        { label: 'ACCESS.GROUP.PAGES', disabled: this.group_id === null },
        { label: 'ACCESS.GROUP.PERMISSIONS', disabled: this.group_id === null },
      ];
      
      return base;
    },
    ...mapGetters(['currentCompanyId', 'currentPeriodId', 'pages']),

    group_name() {
      if (this.group && this.group.id) {
        return this.group.name;
      }

      return this.$t('ACCESS.GROUP.NEW_GROUP');
    }

  },
  emits: ['created', 'updated', 'deleted'],
  async mounted() {

  },
  methods: {

    created_group_page(group_id, page_id) {
      this.local_group.pages.push({
        page_id,
        has_access: true
      });

      this.local_group = { ...this.local_group };
    },

    deleted_group_page(group_id, page_id) {
      this.local_group.pages = this.local_group.pages.filter(item => item.page_id !== page_id)

      this.local_group = { ...this.local_group }
    },

    show() {
      this.$refs['edit-group'].show();
    },

    permissions_changed(permissions) {
      this.local_group.permissions = permissions;
    },

    async save_group_clicked() {
      if (this.local_group.id) {
        await this.put_group(this.local_group);
      }
      else {
        await this.post_group(this.local_group);
      }

      this.$refs['saveButton'].stop();
    },

    group_changed(group) {
      this.local_group = { ...group }

    },

    group_created(group) {
      this.$emit('created', group);
    },

    edit_group_clicked(item) {
      this.local_group = item;
      this.$refs['edit-group'].show();
    },

    delete_group_clicked(item) {
      this.delete_group(item.id);
    },

    new_group_clicked() {
      this.local_group = {};
      this.$refs['edit-group'].show();
    },

    reset() {

      this.local_group = {};
    },


    async get_group(id) {
      try {

        if (!id) {
          return;
        }

        const res = await axios.get(`/access/group/${id}`);

        if (res.status === 200) {
          this.local_group = res.data;

          return;
        }

        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('ACCESS.GROUP.UNABLE_GET'));
      }
      catch (err) {
        console.error(err);
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('ACCESS.GROUP.UNABLE_GET'));
      }
    },

    async delete_group(id) {
      try {
        const res = await axios.delete(`/access/group/${id}`);

        if (res.status === 204) {
          this.toastr('success', this.$t('COMMON.SUCCESS'), this.$t('ACCESS.GROUP.DELETED'));
          this.$emit('deleted', id);

          return;
        }

        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('ACCESS.GROUP.UNABLE_DELETE'));
      }
      catch (err) {
        console.error(err);
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('ACCESS.GROUP.UNABLE_DELETE'));
      }
    },

    async put_group(group) {
      try {
        const res = await axios.put(`/access/group/${group.id}`, group);

        if (res.status === 204) {
          this.toastr('success', this.$t('COMMON.SUCCESS'), this.$t('ACCESS.GROUP.UPDATED'));

          this.$emit('updated', group);

          return;
        }

        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('ACCESS.GROUP.UNABLE_UPDATE'));
      }
      catch (err) {
        console.error(err);
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('ACCESS.GROUP.UNABLE_UPDATE'));
      }
    },

    async post_group(group) {
      try {
        const res = await axios.post(`/access/group`, group);

        if (res.status === 201) {
          this.toastr('success', this.$t('COMMON.SUCCESS'), this.$t('ACCESS.GROUP.CREATED'));

          this.$emit('created', res.data);

          return res.data;
        }

        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('ACCESS.GROUP.UNABLE_CREATE'));
      }
      catch (err) {
        console.error(err);
        this.toastr('danger', this.$t('COMMON.ERROR'), this.$t('ACCESS.GROUP.UNABLE_CREATE'));
      }
    },
  },
  data() {
    return {
      active_tab: 0,
      local_group: {}
    };
  },
  watch: {
    group_id: {
      deep: true, // check properties of the form, not just the form reference (example.name for example)
      handler(val) {
        if (val) {
          this.get_group(this.group_id);
        }
      },
      immediate: true
    },
  },

};
</script>
